import { useQuery } from "@apollo/client"
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { HaufeIcon } from "components/HaufeIcon/HaufeIcon"
import { GET_EMPLOYEES } from "graphql/queries/employees"
import { Device, DeviceEutGroup, Employee, Examination } from "graphql/types"
import { AddCalculation } from "pages/AdminUI/AdminExaminationConfig/AddCalculation"
import { useEffect, useState } from "react"
import { treoLocationList } from "utils/constants"
import { calculateCost } from "utils/generateProjectCalculation"

type TestSetupCalculationProps = {
  devices: DeviceEutGroup[]
  onSave: (arg: Device, isOffer?: boolean, noSnackbar?: boolean) => void
  open: boolean
  setOpen: (arg: boolean) => void
  examination: Examination
}

export const TestSetupCalculation = (props: TestSetupCalculationProps) => {
  const { devices, onSave, open, setOpen, examination } = props
  const [updatedDevices, setUpdatedDevices] = useState<DeviceEutGroup[]>(devices)
  const [updatedExamination, setUpdatedExamination] = useState<Examination>(examination)

  const { data: employeesData } = useQuery(GET_EMPLOYEES)

  useEffect(() => {
    setUpdatedDevices(devices)
  }, [devices])

  useEffect(() => {
    if (examination && updatedDevices.length > 0) {
      const maxOperatingModes = getMaxOperatingModesCount()

      // Handle Connection-based calculations
      let updatedCalculation = examination.calculation
      if (examination.calculation.calculationType === "Connection") {
        // Get all calculation-relevant device data values from all devices
        const allDeviceDataValues = updatedDevices
          .flatMap((device) => device.deviceDataGroup)
          .flatMap((group) => group.deviceData)
          .filter((data) => data.isCalculation && data.dataType === "FLOAT")
          .flatMap((deviceData) =>
            (deviceData.data || []).map((data) => ({
              value: data.value,
              name: deviceData.name,
            }))
          )
          .filter((item) => item.value != null)
          .reduce((acc, item) => {
            const value =
              typeof item.value === "number" ? item.value : parseFloat(String(item.value))
            if (!isNaN(value)) {
              acc.set(item.name, (acc.get(item.name) || 0) + value)
            }
            return acc
          }, new Map<string, number>())

        // Update only zero/empty connection amounts from device data totals, preserve non-zero
        const updatedConnectionPrices = examination.calculation.connectionPrice.map((price) => {
          const deviceDataValue = allDeviceDataValues.get(price.name) || 0
          const amountIsZeroOrEmpty = price.amount == null || price.amount === 0
          return {
            ...price,
            amount: amountIsZeroOrEmpty ? deviceDataValue : price.amount,
          }
        })

        updatedCalculation = {
          ...examination.calculation,
          connectionPrice: updatedConnectionPrices,
        }
      }

      setUpdatedExamination({
        ...examination,
        numberOfRuns: examination.numberOfRuns || maxOperatingModes,
        calculation: updatedCalculation,
      })
    } else if (examination) {
      setUpdatedExamination(examination)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examination])

  const handleSave = () => {
    if (updatedDevices) {
      updatedDevices.forEach((device) => {
        const newDevice = {
          ...device,
          examinations: device.examinations.map((exam) =>
            exam.id === updatedExamination.id
              ? {
                  ...updatedExamination,
                  operatingModes: device.examinations.find(
                    (exam) => exam.id === updatedExamination.id
                  )?.operatingModes,
                  // keep existing ID to avoid breaking links
                  id: updatedExamination.id,
                  salesQuote: updatedExamination.salesQuote || "",
                  tester: updatedExamination.tester ? { ...updatedExamination.tester } : undefined,
                }
              : { ...exam }
          ),
        }
        onSave(newDevice, false, true) // Pass noSnackbar = true to suppress individual success messages
      })
      setOpen(false)
    }
  }

  const handleCancel = () => {
    setUpdatedDevices(devices)
    setOpen(false)
  }

  // Get available operating modes from device's deviceDataGroup
  // Create operating mode objects with label and value
  type OperatingModeOption = {
    label: string
    value: string
  }

  const getAvailableOperatingModes = (device: DeviceEutGroup): OperatingModeOption[] => {
    const modes: OperatingModeOption[] = []

    device.deviceDataGroup.forEach((group) => {
      group.deviceData
        .filter((data) => data.isOperatingMode)
        .forEach((data) => {
          ;(data.data || []).forEach((d) => {
            if (d != null && d.value != null && d.value !== "") {
              modes.push({
                label: d.value,
                value: d.value,
              })
            }
          })
        })
    })

    return modes
  }

  // Calculate the maximum number of operating modes across all devices
  const getMaxOperatingModesCount = (): number => {
    if (!updatedDevices || updatedDevices.length === 0) return 0

    const operatingModesCounts = updatedDevices.map(
      (device) => getAvailableOperatingModes(device).length
    )

    return Math.max(...operatingModesCounts, 0)
  }

  // Update operating modes for a specific device's examination
  const updateDeviceOperatingModes = (deviceId: string, operatingModes: string[]) => {
    const newUpdateDevices = updatedDevices.map((device) => {
      if (device.id === deviceId) {
        return {
          ...device,
          examinations: device.examinations.map((exam) =>
            exam.id === updatedExamination.id ? { ...exam, operatingModes: operatingModes } : exam
          ),
        }
      }
      return device
    })
    setUpdatedDevices(newUpdateDevices)
  }

  if (!examination) {
    return null
  }

  return (
    <Dialog open={open} fullWidth maxWidth="lg">
      <DialogContent sx={{ overflow: "auto", display: "block" }}>
        <Card sx={{ width: "100%", mb: 2 }}>
          <CardHeader
            title={<Typography variant="h6">{examination.name}</Typography>}
            subheader={`nach Norm: ${examination.norm?.name || "Keine Norm"}`}
          />
          <CardContent>
            {/* Examination Properties Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                mb: 3,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Autocomplete
                  size="small"
                  options={treoLocationList}
                  value={updatedExamination.location}
                  onChange={(_, value) => {
                    if (value) {
                      setUpdatedExamination({
                        ...updatedExamination,
                        location: value,
                      })
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          Standort <HaufeIcon />
                        </Box>
                      }
                    />
                  )}
                  renderOption={(props, option) => <li {...props}>{option.name}</li>}
                  getOptionLabel={(option) => option.name}
                />

                <TextField
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      Sonderabsprachen <HaufeIcon />
                    </Box>
                  }
                  name="specialArrangements"
                  value={updatedExamination.specialArrangements}
                  multiline
                  rows={4}
                  fullWidth
                  sx={{ mt: 2, mb: 2 }}
                  onChange={(e) => {
                    setUpdatedExamination({
                      ...updatedExamination,
                      specialArrangements: e.target.value,
                    })
                  }}
                />

                <TextField
                  label={"Abweichungen"}
                  name="deviations"
                  value={updatedExamination.deviations || ""}
                  multiline
                  rows={3}
                  fullWidth
                  sx={{ mt: 2, mb: 2 }}
                  placeholder="Dokumentieren Sie Abweichungen vom Standardverfahren..."
                  onChange={(e) => {
                    setUpdatedExamination({
                      ...updatedExamination,
                      deviations: e.target.value,
                    })
                  }}
                />

                <Autocomplete
                  size="small"
                  options={employeesData?.getEmployees || []}
                  value={
                    employeesData?.getEmployees?.find(
                      (emp: Employee) => emp.id === updatedExamination.tester?.id
                    ) || null
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_, value) => {
                    setUpdatedExamination({
                      ...updatedExamination,
                      tester: value || undefined,
                    })
                  }}
                  renderInput={(params) => <TextField {...params} label="Prüfer" sx={{ mt: 2 }} />}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option.EmployeeName ||
                        option.ContactInfo?.FirstName + " " + option.ContactInfo?.LastName ||
                        "Unknown"}
                    </li>
                  )}
                  getOptionLabel={(option) =>
                    option.EmployeeName ||
                    option.ContactInfo?.FirstName + " " + option.ContactInfo?.LastName ||
                    "Unknown"
                  }
                />

                <FormGroup sx={{ mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={updatedExamination.isAccredited}
                        onChange={(e) => {
                          setUpdatedExamination({
                            ...updatedExamination,
                            isAccredited: e.target.checked,
                          })
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        Akkreditiert <HaufeIcon />
                      </Box>
                    }
                  />
                </FormGroup>
              </Box>

              <Box sx={{ flex: 1 }}>
                <AddCalculation
                  noDelete={true}
                  disabledName={true}
                  formState={updatedExamination.calculation}
                  setFormState={(value) => {
                    setUpdatedExamination({
                      ...updatedExamination,
                      calculation: value,
                    })
                  }}
                  deviceConnectionTotals={
                    updatedExamination.calculation.calculationType === "Connection"
                      ? (() => {
                          // Calculate device connection totals
                          const deviceConnectionTotals = updatedDevices
                            .flatMap((device) => device.deviceDataGroup)
                            .flatMap((group) => group.deviceData)
                            .filter((data) => data.isCalculation && data.dataType === "FLOAT")
                            .flatMap((deviceData) =>
                              (deviceData.data || []).map((data) => ({
                                value: data.value,
                                name: deviceData.name,
                              }))
                            )
                            .filter((item) => item.value != null)
                            .reduce((acc, item) => {
                              const value =
                                typeof item.value === "number"
                                  ? item.value
                                  : parseFloat(String(item.value))
                              if (!isNaN(value)) {
                                acc.set(item.name, (acc.get(item.name) || 0) + value)
                              }
                              return acc
                            }, new Map<string, number>())
                          return deviceConnectionTotals
                        })()
                      : undefined
                  }
                />

                <TextField
                  label={"Anzahl der Durchläufe"}
                  name="numberOfRuns"
                  type="number"
                  value={updatedExamination.numberOfRuns || 1}
                  fullWidth
                  sx={{ mt: 2, mb: 2 }}
                  onChange={(e) => {
                    const numberOfRuns = parseInt(e.target.value) || 1
                    setUpdatedExamination({
                      ...updatedExamination,
                      numberOfRuns: numberOfRuns,
                    })
                  }}
                />

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                    Kostenübersicht
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Preis</TableCell>
                          <TableCell>Kosten</TableCell>
                          <TableCell>Marge</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            {calculateCost(
                              updatedExamination.calculation,
                              updatedExamination.numberOfRuns || 0
                            ).price.toFixed(2)}{" "}
                            €
                          </TableCell>
                          <TableCell>
                            {calculateCost(
                              updatedExamination.calculation,
                              updatedExamination.numberOfRuns || 0
                            ).cost.toFixed(2)}{" "}
                            €
                          </TableCell>
                          <TableCell>
                            {calculateCost(
                              updatedExamination.calculation,
                              updatedExamination.numberOfRuns || 0
                            ).margin.toFixed(2)}{" "}
                            €
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </Box>

            {/* Devices Section */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                EUTs in diesem TestSetup:
              </Typography>
              {updatedDevices.map((device) => {
                const availableOperatingModes = getAvailableOperatingModes(device)
                const deviceExamination = device.examinations.find(
                  (exam) => exam.id === updatedExamination.id
                )
                // Convert current operating mode values to the option objects
                const currentOperatingModeValues = deviceExamination?.operatingModes || []
                const currentOperatingModes = availableOperatingModes.filter((mode) =>
                  currentOperatingModeValues.includes(mode.value)
                )

                return (
                  <Card
                    key={device.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {device.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Barcode: {device.barcode}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {device.deviceDataGroup
                            .flatMap((group) => group.deviceData)
                            .filter((data) => data.isCalculation && data.dataType === "FLOAT")
                            .flatMap((deviceData) =>
                              (deviceData.data || []).map((data) => ({
                                value: data.value,
                                name: deviceData.name,
                              }))
                            )
                            .filter((item) => item.value != null)
                            .map((item) => {
                              const value =
                                typeof item.value === "number"
                                  ? item.value
                                  : parseFloat(String(item.value))
                              return !isNaN(value) ? `${value} x ${item.name}` : null
                            })
                            .filter((item) => item !== null)
                            .join(", ")}
                        </Typography>
                      </Box>
                      <Box sx={{ maxWidth: "500px", minWidth: "300px" }}>
                        <Autocomplete
                          multiple
                          fullWidth
                          size="small"
                          options={availableOperatingModes}
                          value={currentOperatingModes}
                          getOptionLabel={(option) => option.label}
                          isOptionEqualToValue={(option, value) => option.value === value.value}
                          onChange={(_, newValue) => {
                            // Extract just the values for storage
                            const values = newValue.map((option) => option.value)
                            updateDeviceOperatingModes(device.id, values)
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Betriebsmodi"
                              placeholder="Betriebsmodi auswählen..."
                              InputProps={{
                                ...params.InputProps,
                                sx: {
                                  minHeight: "auto",
                                  height: "auto",
                                  flexWrap: "wrap",
                                  "& .MuiAutocomplete-input": {
                                    minWidth: "30px",
                                  },
                                },
                              }}
                            />
                          )}
                          renderOption={(props, option) => <li {...props}>{option.label}</li>}
                          sx={{
                            mt: 1,
                            "& .MuiAutocomplete-inputRoot": {
                              flexWrap: "wrap",
                              minHeight: "40px",
                              maxHeight: "none",
                              height: "auto",
                              paddingTop: "6px",
                              paddingBottom: "6px",
                            },
                            "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
                              minWidth: "30px",
                              width: "auto",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Card>
                )
              })}
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="save" onClick={handleSave} />
        <StyledButton buttonType="cancel" onClick={handleCancel} />
      </DialogActions>
    </Dialog>
  )
}
